import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, ArrowRight, Target, Gauge, TrendingUp, Leaf, Network, Compass } from "lucide-react";
import { useSite, tx } from "@/lib/lang";
import { ui } from "@/lib/uiStrings";
import { Reveal, SectionLabel } from "./Reveal";
import SectionTabs from "./SectionTabs";

const txl = (field, lang) => field?.[lang] || field?.it || [];

// Icona per focus (chapter.num). Solo presentazione: i contenuti restano dal CMS.
const CH_ICONS = {
  "01": Target,
  "02": Gauge,
  "03": TrendingUp,
  "04": Leaf,
  "05": Network,
};

function ChapterPanel({ ch, mode, lang, labels }) {
  const isNew = mode === "new";
  const lead = tx(isNew ? ch.new_lead : ch.old_lead, lang);
  const desc = tx(ch.old_desc, lang);
  const points = txl(isNew ? ch.new_points : ch.old_points, lang);
  return (
    <div data-testid={`manifesto-chapter-${ch.num}`}>
      <div className="mb-4">
        <span
          className={`inline-block font-mono text-[11px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${
            isNew ? "bg-royal/15 text-azure" : "bg-ice/10 text-muted-foreground"
          }`}
        >
          {isNew ? labels.new : labels.old}
        </span>
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={mode}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
        >
          <p className={`font-serif italic text-lg leading-snug ${isNew ? "text-azure" : "text-muted-foreground"}`}>{lead}</p>
          {!isNew && desc && <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{desc}</p>}
          <ul className="mt-5 space-y-2.5">
            {points.map((p, i) => (
              <li
                key={i}
                className={`flex items-start gap-3 text-sm ${
                  isNew ? "text-ice/85" : "text-muted-foreground/60 line-through decoration-ice/20"
                }`}
              >
                {isNew ? (
                  <Check size={15} className="mt-0.5 text-azure shrink-0" />
                ) : (
                  <X size={15} className="mt-0.5 text-muted-foreground/35 shrink-0" />
                )}
                {p}
              </li>
            ))}
          </ul>
          {isNew && tx(ch.key_msg, lang) && (
            <p
              data-testid={`manifesto-keymsg-${ch.num}`}
              className="mt-6 border-l-2 border-royal pl-4 py-2.5 pr-4 rounded-r-lg bg-royal/5 text-sm font-medium text-ice leading-relaxed"
            >
              {tx(ch.key_msg, lang)}
            </p>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function ModelsVisual({ mode, visual, lang }) {
  const oldItems = txl(visual?.old_items, lang);
  const newItems = txl(visual?.new_items, lang);
  const flow = txl(visual?.new_flow, lang);
  return (
    <div className="mt-14 border-t border-ice/8 pt-12" data-testid="manifesto-models-visual">
      <AnimatePresence mode="wait">
        {mode === "old" ? (
          <motion.div key="old" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}>
            <div className="flex flex-wrap gap-3">
              {oldItems.map((item, i) => {
                const [k, v] = item.split("→").map((s) => s.trim());
                return (
                  <span
                    key={i}
                    className="inline-flex items-center gap-2 border border-dashed border-ice/20 rounded-lg px-4 py-2.5 text-sm text-muted-foreground bg-onyx/40"
                  >
                    <span className="font-semibold text-ice/70">{k}</span>
                    <ArrowRight size={13} className="text-muted-foreground/40" />
                    <span>{v}</span>
                  </span>
                );
              })}
            </div>
            <p className="mt-7 font-serif italic text-lg text-muted-foreground max-w-2xl">{tx(visual?.old_msg, lang)}</p>
          </motion.div>
        ) : (
          <motion.div key="new" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}>
            <div className="flex flex-col items-center gap-0">
              <span className="font-display font-bold text-white bg-royal rounded-full px-6 py-3 text-sm shadow-lg shadow-royal/20">
                {tx(visual?.new_center, lang)}
              </span>
              <div className="w-px h-6 bg-royal/40" />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 w-full">
                {newItems.map((item, i) => (
                  <span
                    key={i}
                    className="text-center text-xs sm:text-sm font-medium text-ice bg-surface border border-royal/25 rounded-xl px-3 py-3.5 leading-snug"
                  >
                    {item}
                  </span>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-x-2.5 gap-y-2">
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-royal mr-1">
                  {tx(visual?.new_regia_label, lang)}:
                </span>
                {flow.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-2.5">
                    <span className="font-display font-semibold text-sm text-ice">{f}</span>
                    {i < flow.length - 1 && <ArrowRight size={13} className="text-azure" />}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Manifesto() {
  const { content, lang } = useSite();
  const t0 = ui[lang].manifesto;
  const sz = content.sezioni?.manifesto || {};
  const t = {
    heading: tx(sz.heading, lang) || t0.heading,
    sub: tx(sz.sub, lang) || t0.sub,
    intro: tx(sz.intro, lang) || t0.intro,
    old: tx(sz.old, lang) || t0.old,
    new: tx(sz.new, lang) || t0.new,
  };
  const chapters = content.chapters || [];
  const visual = content.manifesto_visual || {};
  const [mode, setMode] = useState("new");

  const chapterItems = chapters.map((ch) => {
    const Icon = CH_ICONS[ch.num] || Compass;
    return {
      id: ch.num,
      badge: <Icon size={18} />,
      title: tx(ch.title, lang),
      subtitle: tx(ch.new_lead, lang),
      render: () => <ChapterPanel ch={ch} mode={mode} lang={lang} labels={{ new: t.new, old: t.old }} />,
    };
  });

  return (
    <section id="manifesto" data-testid="manifesto-section" className="py-16 lg:py-24 bg-surface/30 border-y border-ice/5">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <SectionLabel>{ui[lang].labels.manifesto}</SectionLabel>
            <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-ice">
              {t.heading}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">{t.sub}</p>
          </div>
          <div data-testid="manifesto-toggle-mode" className="relative flex border border-ice/10 rounded-full p-1 bg-onyx/60 w-fit">
            {[
              ["old", t.old],
              ["new", t.new],
            ].map(([key, label]) => (
              <button
                key={key}
                data-testid={`manifesto-mode-${key}`}
                onClick={() => setMode(key)}
                className={`relative px-5 py-2 rounded-full text-sm font-medium transition-colors ${mode === key ? "text-white" : "text-muted-foreground hover:text-ice"}`}
              >
                {mode === key && (
                  <motion.span layoutId="manifesto-pill" className={`absolute inset-0 rounded-full ${key === "new" ? "bg-royal" : "bg-ice/10"}`} transition={{ type: "spring", stiffness: 350, damping: 30 }} />
                )}
                <span className="relative z-10">{label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05} className="mt-8 max-w-3xl">
          {(t.intro || "").split("\n\n").filter(Boolean).map((p, i) => (
            <p key={i} className={`text-base leading-relaxed text-muted-foreground ${i > 0 ? "mt-4" : ""}`}>
              {p}
            </p>
          ))}
        </Reveal>

        <Reveal delay={0.05} className="mt-12">
          <SectionTabs items={chapterItems} ariaLabel={lang === "it" ? "Focus del manifesto" : "Manifesto focus"} testIdPrefix="manifesto-focus" />
        </Reveal>

        <Reveal delay={0.05}>
          <ModelsVisual mode={mode} visual={visual} lang={lang} />
        </Reveal>
      </div>
    </section>
  );
}
